/**
 * Sprite selezionabili per la sfida "Prendi la talpa".
 * SVG inline o immagini esterne, scalabili a qualsiasi dimensione.
 */

export interface MoleSprite {
  id: string;
  name: string;
  glowColor: string; // CSS boxShadow glow
  render: (size: number) => JSX.Element;
}

export const MOLE_SPRITES: MoleSprite[] = [
  // Cerchio rosso (default originale)
  {
    id: 'default',
    name: 'Cerchio',
    glowColor: 'rgba(255, 107, 107, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
        <defs>
          <radialGradient id="mole-default-grad" cx="35%" cy="35%" r="50%">
            <stop offset="0%" stopColor="#ff6b6b" />
            <stop offset="100%" stopColor="#c0392b" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="30" fill="url(#mole-default-grad)" />
      </svg>
    ),
  },

  // Ragno
  {
    id: 'spider',
    name: 'Ragno',
    glowColor: 'rgba(139, 92, 246, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
        strokeLinecap="round" strokeLinejoin="round">
        {/* Corpo */}
        <ellipse cx="32" cy="30" rx="10" ry="9" fill="#1a1a2e" stroke="#8b5cf6" strokeWidth="2.5" />
        {/* Addome */}
        <ellipse cx="32" cy="42" rx="8" ry="7" fill="#1a1a2e" stroke="#8b5cf6" strokeWidth="2.5" />
        {/* Occhi */}
        <circle cx="28" cy="28" r="2.5" fill="#ff4444" />
        <circle cx="36" cy="28" r="2.5" fill="#ff4444" />
        <circle cx="25" cy="31" r="1.5" fill="#ff4444" />
        <circle cx="39" cy="31" r="1.5" fill="#ff4444" />
        {/* Zampe sinistre */}
        <path d="M22 26 Q14 18 8 16" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M22 30 Q12 28 6 25" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M24 38 Q14 40 7 38" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M26 44 Q16 50 9 52" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        {/* Zampe destre */}
        <path d="M42 26 Q50 18 56 16" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M42 30 Q52 28 58 25" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M40 38 Q50 40 57 38" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
        <path d="M38 44 Q48 50 55 52" stroke="#8b5cf6" strokeWidth="2.2" fill="none" />
      </svg>
    ),
  },

  // Fiamma
  {
    id: 'flame',
    name: 'Fiamma',
    glowColor: 'rgba(251, 146, 60, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
        strokeLinecap="round" strokeLinejoin="round">
        {/* Fiamma esterna */}
        <path
          d="M32 4 C32 4 18 22 18 38 C18 48 24 58 32 60 C40 58 46 48 46 38 C46 22 32 4 32 4Z"
          fill="#f97316" stroke="#ea580c" strokeWidth="2.5"
        />
        {/* Fiamma interna */}
        <path
          d="M32 18 C32 18 24 30 24 40 C24 46 28 52 32 54 C36 52 40 46 40 40 C40 30 32 18 32 18Z"
          fill="#fbbf24" stroke="#f59e0b" strokeWidth="1.5"
        />
        {/* Cuore */}
        <path
          d="M32 32 C32 32 28 38 28 42 C28 46 30 50 32 50 C34 50 36 46 36 42 C36 38 32 32 32 32Z"
          fill="#fef3c7" stroke="#fcd34d" strokeWidth="1"
        />
        {/* Scintille */}
        <circle cx="22" cy="20" r="1.5" fill="#fbbf24" opacity="0.8" />
        <circle cx="42" cy="24" r="1.2" fill="#fbbf24" opacity="0.7" />
        <circle cx="26" cy="14" r="1" fill="#fcd34d" opacity="0.6" />
      </svg>
    ),
  },

  // Goccia d'acqua
  {
    id: 'water',
    name: 'Goccia',
    glowColor: 'rgba(59, 130, 246, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
        strokeLinecap="round" strokeLinejoin="round">
        {/* Goccia principale */}
        <path
          d="M32 6 C32 6 14 28 14 40 C14 50 22 58 32 58 C42 58 50 50 50 40 C50 28 32 6 32 6Z"
          fill="#3b82f6" stroke="#2563eb" strokeWidth="2.5"
        />
        {/* Riflesso */}
        <path
          d="M24 36 Q22 42 26 48"
          stroke="#93c5fd" strokeWidth="2.5" fill="none" opacity="0.7"
        />
        <path
          d="M22 40 Q21 43 23 45"
          stroke="#bfdbfe" strokeWidth="1.5" fill="none" opacity="0.5"
        />
        {/* Highlight */}
        <ellipse cx="26" cy="32" rx="3" ry="5" fill="#93c5fd" opacity="0.4" />
        {/* Gocciolina schizzata */}
        <circle cx="44" cy="18" r="2" fill="#60a5fa" opacity="0.6" />
        <circle cx="20" cy="22" r="1.5" fill="#60a5fa" opacity="0.5" />
      </svg>
    ),
  },

  // Scudo
  {
    id: 'shield',
    name: 'Scudo',
    glowColor: 'rgba(234, 179, 8, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
        strokeLinecap="round" strokeLinejoin="round">
        {/* Scudo forma */}
        <path
          d="M32 4 L10 16 L10 36 C10 48 20 58 32 62 C44 58 54 48 54 36 L54 16 Z"
          fill="#854d0e" stroke="#a16207" strokeWidth="2.5"
        />
        {/* Bordo interno */}
        <path
          d="M32 10 L16 20 L16 36 C16 45 23 53 32 56 C41 53 48 45 48 36 L48 20 Z"
          fill="#ca8a04" stroke="#a16207" strokeWidth="1.5"
        />
        {/* Croce */}
        <line x1="32" y1="18" x2="32" y2="50" stroke="#fef3c7" strokeWidth="3" />
        <line x1="20" y1="32" x2="44" y2="32" stroke="#fef3c7" strokeWidth="3" />
        {/* Borchie */}
        <circle cx="16" cy="20" r="2" fill="#fbbf24" />
        <circle cx="48" cy="20" r="2" fill="#fbbf24" />
        <circle cx="32" cy="10" r="2" fill="#fbbf24" />
        <circle cx="16" cy="36" r="2" fill="#fbbf24" />
        <circle cx="48" cy="36" r="2" fill="#fbbf24" />
      </svg>
    ),
  },

  // Pozione di mana
  {
    id: 'potion',
    name: 'Pozione',
    glowColor: 'rgba(168, 85, 247, 0.6)',
    render: (size) => (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
        strokeLinecap="round" strokeLinejoin="round">
        {/* Collo bottiglia */}
        <rect x="27" y="6" width="10" height="12" rx="2" fill="#6b7280" stroke="#4b5563" strokeWidth="2" />
        {/* Tappo */}
        <rect x="26" y="4" width="12" height="5" rx="2" fill="#92400e" stroke="#78350f" strokeWidth="1.5" />
        {/* Corpo bottiglia */}
        <path
          d="M27 18 L20 28 C16 34 16 38 16 42 C16 52 22 58 32 58 C42 58 48 52 48 42 C48 38 48 34 44 28 L37 18 Z"
          fill="#7c3aed" stroke="#6d28d9" strokeWidth="2.5"
        />
        {/* Liquido dentro */}
        <path
          d="M22 34 C22 34 20 38 20 42 C20 50 25 55 32 55 C39 55 44 50 44 42 C44 38 42 34 42 34 Z"
          fill="#a855f7" stroke="none"
        />
        {/* Bollicine */}
        <circle cx="28" cy="44" r="2" fill="#c084fc" opacity="0.7" />
        <circle cx="35" cy="40" r="1.5" fill="#c084fc" opacity="0.6" />
        <circle cx="30" cy="48" r="1" fill="#d8b4fe" opacity="0.5" />
        <circle cx="37" cy="46" r="1.5" fill="#c084fc" opacity="0.5" />
        {/* Riflesso vetro */}
        <path d="M24 28 L24 42" stroke="#a78bfa" strokeWidth="1.5" opacity="0.4" />
        {/* Stellina magica */}
        <path d="M32 30 L33 33 L36 33 L34 35 L35 38 L32 36 L29 38 L30 35 L28 33 L31 33 Z"
          fill="#fde68a" stroke="#fbbf24" strokeWidth="0.8" />
      </svg>
    ),
  },

  // Mostrino Onirico
  {
    id: 'mostrino-onirico',
    name: 'Onirico',
    glowColor: 'rgba(168, 85, 247, 0.6)',
    render: (size) => (
      <img src="/images/mostrino-onirico.png" width={size} height={size} alt="Mostrino Onirico" draggable={false} />
    ),
  },

  // Mostrino Ghiaccio
  {
    id: 'mostrino-ghiaccio',
    name: 'Ghiaccio',
    glowColor: 'rgba(96, 165, 250, 0.6)',
    render: (size) => (
      <img src="/images/mostrino-ghiaccio.png" width={size} height={size} alt="Mostrino Ghiaccio" draggable={false} />
    ),
  },

  // Mostrino Acido
  {
    id: 'mostrino-acido',
    name: 'Acido',
    glowColor: 'rgba(74, 222, 128, 0.6)',
    render: (size) => (
      <img src="/images/mostrino-acido.png" width={size} height={size} alt="Mostrino Acido" draggable={false} />
    ),
  },
];

export function getSpriteById(id: string): MoleSprite {
  return MOLE_SPRITES.find(s => s.id === id) || MOLE_SPRITES[0];
}

export function getAllSprites(): MoleSprite[] {
  return MOLE_SPRITES;
}
